import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { updateProfile, changePassword } from '../../services/api';
import toast from 'react-hot-toast';
import { User, Lock, Save } from 'lucide-react';

export default function UserProfile() {
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState({ first_name: user?.first_name || '', last_name: user?.last_name || '', phone: user?.phone || '', mobility_needs: user?.mobility_needs || '', emergency_contact_name: user?.emergency_contact_name || '', emergency_contact_phone: user?.emergency_contact_phone || '' });
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile(profile);
      setUser(u => ({ ...u, ...profile }));
      toast.success('Profile updated!');
    } catch { toast.error('Update failed.'); }
    finally { setSaving(false); }
  };

  const handlePwChange = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) { toast.error('Passwords do not match.'); return; }
    setChangingPw(true);
    try {
      await changePassword({ current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success('Password changed!');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setChangingPw(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl animate-fade-up">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 font-heading">My Profile</h1>
        <p className="text-sm text-gray-500">Manage your personal information and preferences</p>
      </div>

      {/* Avatar header */}
      <div className="card border border-gray-100 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-2xl">
          {user?.first_name?.[0]}{user?.last_name?.[0]}
        </div>
        <div>
          <p className="font-bold text-gray-900 text-lg">{user?.first_name} {user?.last_name}</p>
          <p className="text-gray-500 text-sm">{user?.email}</p>
          <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium capitalize">{user?.role}</span>
        </div>
      </div>

      {/* Profile form */}
      <form onSubmit={handleProfileSave} className="card border border-gray-100">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 bg-purple-100 rounded-xl flex items-center justify-center"><User size={16} className="text-purple-700" /></div>
          <h2 className="font-bold text-gray-900 font-heading">Personal Information</h2>
        </div>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label">First Name</label>
              <input className="input-field" value={profile.first_name} onChange={e => setProfile({ ...profile, first_name: e.target.value })} />
            </div>
            <div>
              <label className="label">Last Name</label>
              <input className="input-field" value={profile.last_name} onChange={e => setProfile({ ...profile, last_name: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="label">Phone Number</label>
            <input className="input-field" value={profile.phone} onChange={e => setProfile({ ...profile, phone: e.target.value })} />
          </div>
          <div>
            <label className="label">Mobility Needs / Medical Notes</label>
            <textarea className="input-field min-h-[80px] resize-none" placeholder="e.g., Uses a walker, requires extra boarding time..." value={profile.mobility_needs} onChange={e => setProfile({ ...profile, mobility_needs: e.target.value })} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Emergency Contact Name</label>
              <input className="input-field" value={profile.emergency_contact_name} onChange={e => setProfile({ ...profile, emergency_contact_name: e.target.value })} />
            </div>
            <div>
              <label className="label">Emergency Contact Phone</label>
              <input className="input-field" value={profile.emergency_contact_phone} onChange={e => setProfile({ ...profile, emergency_contact_phone: e.target.value })} />
            </div>
          </div>
        </div>
        <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2 mt-5">
          {saving ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Save size={16} />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </form>

      {/* Password form */}
      <form onSubmit={handlePwChange} className="card border border-gray-100">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 bg-gray-100 rounded-xl flex items-center justify-center"><Lock size={16} className="text-gray-600" /></div>
          <h2 className="font-bold text-gray-900 font-heading">Change Password</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="label">Current Password</label>
            <input type="password" className="input-field" required value={pwForm.current_password} onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })} />
          </div>
          <div>
            <label className="label">New Password</label>
            <input type="password" className="input-field" required minLength={8} value={pwForm.new_password} onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })} />
          </div>
          <div>
            <label className="label">Confirm New Password</label>
            <input type="password" className="input-field" required value={pwForm.confirm_password} onChange={e => setPwForm({ ...pwForm, confirm_password: e.target.value })} />
          </div>
        </div>
        <button type="submit" disabled={changingPw} className="mt-5 flex items-center gap-2 bg-gray-800 hover:bg-gray-900 text-white font-semibold py-2.5 px-5 rounded-lg transition-colors">
          {changingPw ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Lock size={16} />}
          {changingPw ? 'Updating...' : 'Update Password'}
        </button>
      </form>
    </div>
  );
}
